import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClient } from './apiClient';
import { useApiMutation } from './useApiMutation';
import { useApiResource } from './useApiResource';

/**
 * One page of a cursor-paginated endpoint. `nextCursor` is `null` on the last
 * page — the only end-of-list signal these APIs give, since none of them
 * returns a total (a `COUNT(*)` over an append-only table is stale by the time
 * it renders).
 */
export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface UseCursorListResult<T> {
  /** Every page loaded so far, in order. */
  items: T[];
  /** True while the FIRST page is in flight (filters changed, or mount). */
  loading: boolean;
  /** True while a **Load more** page is in flight. */
  loadingMore: boolean;
  error: string | null;
  /** True when the server said there is another page. */
  hasMore: boolean;
  loadMore: () => Promise<void>;
  /** Re-read from page one, discarding everything already loaded. */
  reload: () => Promise<void>;
}

export interface UseCursorListOptions {
  skip?: boolean;
  errorPrefix?: string;
  apiClientOverride?: ApiClient;
}

/**
 * Accumulating reader for the `{items, nextCursor}` endpoints — the audit
 * browser (`GET /audit-logs`) and the notification delivery log / domain-event
 * list (`GET /notifications/admin/{deliveries,events}`).
 *
 * It is a third primitive alongside {@link useApiResource} and
 * {@link useApiMutation} rather than a helper inside either feature, because
 * both features need identical behaviour and the interesting part is the same
 * in both: a **Load more** list is not a resource that gets replaced, it is
 * pages that get appended — and the reset rule ("changing a filter throws the
 * accumulation away") is exactly the bug you get if each feature re-derives it.
 *
 * Composed from the two existing primitives rather than hand-rolling a third
 * fetch state machine: page one is a `useApiResource` keyed on `deps`, so it
 * inherits the race-safe fetch-id guard, and each subsequent page runs through
 * `useApiMutation.run` for its own `loadingMore` / error surface. A UI can
 * therefore show a spinner on the **Load more** button without blanking the
 * rows already on screen.
 *
 * `fetchPage` receives `cursor: null` for the first page. Give it a stable
 * identity (`useCallback`) or list everything it closes over in `deps` — the
 * same contract as `useApiResource`.
 *
 * @example
 * const rows = useCursorList<AuditLogRow>(
 *   useCallback(
 *     (client, cursor) => client.get<CursorPage<AuditLogRow>>(buildUrl(filters, cursor)),
 *     [filters],
 *   ),
 *   [filters],
 *   { errorPrefix: 'Failed to load the audit log' },
 * );
 */
export function useCursorList<T>(
  fetchPage: (apiClient: ApiClient, cursor: string | null) => Promise<CursorPage<T>>,
  deps: ReadonlyArray<unknown>,
  options: UseCursorListOptions = {},
): UseCursorListResult<T> {
  const { skip = false, errorPrefix = 'Failed to load more', apiClientOverride } = options;

  const firstPage = useApiResource<CursorPage<T>>(
    useCallback((client: ApiClient) => fetchPage(client, null), [fetchPage]),
    deps,
    { skip, errorPrefix, apiClientOverride },
  );

  const {
    loading: loadingMore,
    error: loadMoreError,
    run,
  } = useApiMutation({ apiClientOverride });

  const [morePages, setMorePages] = useState<CursorPage<T>[]>([]);

  // A new page-one response supersedes everything appended to the previous one.
  // Keyed on the response object, which `useApiResource` replaces on every
  // successful fetch — so a filter change, a `reload()`, and a `refetch()` after
  // a mutation all reset identically.
  //
  // Clearing alone is NOT enough, which is the subtle part. It resets what has
  // already been appended; it says nothing about a `loadMore()` that is still in
  // flight. Narrowing a filter mid-scroll races two requests, and the narrower
  // one is often the quicker: page one for filter B lands and clears the list,
  // then the older page two for filter A resolves and appends **A's rows
  // underneath B's** — with `nextCursor` then advancing from A's stale page. The
  // list silently mixes two queries and nothing reports an error. On the audit
  // browser that is the compliance record showing rows the filter excludes.
  //
  // So each page-one response opens a new generation, and a `loadMore` whose
  // generation has moved on discards its result. Same idea as the `fetchIdRef`
  // guard inside `useApiResource`, which protects page-one fetches from each
  // other but knows nothing about these.
  const firstPageData = firstPage.data;
  const generationRef = useRef(0);
  useEffect(() => {
    generationRef.current += 1;
    setMorePages([]);
  }, [firstPageData]);

  const lastPage: CursorPage<T> | null =
    morePages.length > 0 ? morePages[morePages.length - 1] : firstPageData;
  const nextCursor = lastPage?.nextCursor ?? null;

  const loadMore = useCallback(async () => {
    if (nextCursor === null) return;
    const generation = generationRef.current;
    const page = await run(
      (client) => fetchPage(client, nextCursor),
      errorPrefix,
    );
    // Superseded while in flight — this page belongs to a query the caller has
    // already moved off. Dropped rather than appended.
    if (generation !== generationRef.current) return;
    setMorePages((prev) => [...prev, page]);
  }, [errorPrefix, fetchPage, nextCursor, run]);

  // Destructured, not read off `firstPage` inside the callback: the hook returns
  // a fresh object each render, so closing over it would give `reload` a new
  // identity every time and every consumer memoising on it would re-run.
  const { refetch } = firstPage;
  const reload = useCallback(async () => {
    // `morePages` is cleared by the effect above once the new page-one response
    // lands, so a failed reload leaves the current list intact rather than
    // emptying the table and then reporting an error against nothing.
    await refetch();
  }, [refetch]);

  const items = firstPageData
    ? [...firstPageData.items, ...morePages.flatMap((page) => page.items)]
    : [];

  return {
    items,
    loading: firstPage.loading,
    loadingMore,
    // The page-one error leads: with no first page there are no rows, which is
    // the more consequential failure to report.
    error: firstPage.error ?? loadMoreError,
    hasMore: nextCursor !== null,
    loadMore,
    reload,
  };
}
